import { Copy, ExternalLink, Eye, EyeOff, History, Pencil, StickyNote, Trash2 } from "lucide-react"
import { useState } from "react"

import { Boton } from "@/componentes/ui/Boton"
import { Insignia } from "@/componentes/ui/Insignia"
import { comoFecha, urgenciaDe } from "@/lib/fechas"

import { MedidorFuerza } from "./MedidorFuerza"
import { BORRADO_MS, copiar } from "./portapapeles"
import type { Credencial, Revelada } from "./tipos"

/** Lo que se pinta en lugar de la clave. Fijo: ni el largo se insinúa. */
const MASCARA = "••••••••"

/**
 * Una ficha del cofre.
 *
 * ⚠ **La contraseña no está en la página hasta que se revela.** La máscara es un texto
 * fijo, no la clave tapada con CSS: lo que no está en el DOM no se lee con el
 * inspector ni lo recoge una extensión. Revelada, vuelve sola a la máscara a los 20
 * segundos (el reloj lo lleva la página, que es quien sabe cuántas hay a la vista).
 */
export function TarjetaCredencial({
  credencial,
  revelada,
  revelando,
  puedeEditar,
  onRevelar,
  onOcultar,
  onEditar,
  onBorrar,
  onBitacora,
}: {
  credencial: Credencial
  revelada: Revelada | undefined
  revelando: boolean
  puedeEditar: boolean
  onRevelar: () => void
  onOcultar: () => void
  onEditar: () => void
  onBorrar: () => void
  onBitacora: () => void
}) {
  const [aviso, setAviso] = useState<string | null>(null)

  async function copiarCon(texto: string, hecho: string, secreto = false) {
    const ok = await copiar(texto, secreto)
    setAviso(ok ? hecho : "El navegador no dejó copiar: selecciónalo a mano.")
    window.setTimeout(() => setAviso(null), 4000)
  }

  const vence = credencial.vence_en === null ? null : urgenciaDe(credencial.vence_en)

  return (
    <article className="tarjeta flex flex-col gap-3" aria-label={credencial.nombre}>
      <header className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="font-semibold break-words">{credencial.nombre}</h3>
          <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
            {credencial.categoria}
            {credencial.cliente === null ? "" : ` · ${credencial.cliente}`}
          </p>
        </div>
        {vence !== null && <Insignia tono={vence.tono}>{vence.texto}</Insignia>}
      </header>

      {credencial.url !== null && (
        <a
          href={credencial.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 self-start text-sm break-all underline underline-offset-2"
          style={{ color: "var(--primary-texto)" }}
        >
          {credencial.url.replace(/^https?:\/\//, "")}
          <ExternalLink size={14} aria-hidden={true} />
        </a>
      )}

      <dl className="grid gap-2 text-sm">
        <Dato
          etiqueta="Usuario"
          valor={credencial.usuario}
          onCopiar={() => void copiarCon(credencial.usuario, "Usuario copiado")}
        />
        {credencial.documento !== null && (
          <Dato
            etiqueta="Cédula o RIF"
            valor={credencial.documento}
            onCopiar={() => void copiarCon(credencial.documento ?? "", "Documento copiado")}
          />
        )}
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <dt className="text-xs" style={{ color: "var(--fg-muted)" }}>
              Contraseña
            </dt>
            <dd className="font-mono break-all">
              {revelada === undefined ? (
                <>
                  <span aria-hidden={true}>{MASCARA}</span>
                  <span className="sr-only">Oculta</span>
                </>
              ) : (
                revelada.clave
              )}
            </dd>
          </div>
          <div className="flex shrink-0 gap-1">
            {revelada !== undefined && (
              <button
                type="button"
                onClick={() =>
                  void copiarCon(
                    revelada.clave,
                    `Contraseña copiada: se borra del portapapeles en ${BORRADO_MS / 1000} s`,
                    true,
                  )
                }
                aria-label="Copiar la contraseña"
                className="boton-secundario p-2"
              >
                <Copy size={16} aria-hidden={true} />
              </button>
            )}
            <button
              type="button"
              onClick={revelada === undefined ? onRevelar : onOcultar}
              disabled={revelando}
              aria-label={
                revelada === undefined ? "Mostrar la contraseña" : "Ocultar la contraseña"
              }
              className="boton-secundario p-2"
            >
              {revelada === undefined ? (
                <Eye size={16} aria-hidden={true} />
              ) : (
                <EyeOff size={16} aria-hidden={true} />
              )}
            </button>
          </div>
        </div>
        {revelada?.notas && (
          <div>
            <dt className="text-xs" style={{ color: "var(--fg-muted)" }}>
              Notas
            </dt>
            <dd className="break-words whitespace-pre-wrap">{revelada.notas}</dd>
          </div>
        )}
        {credencial.correo !== null && <Dato etiqueta="Correo" valor={credencial.correo} />}
        {credencial.telefono !== null && (
          <Dato etiqueta="Teléfono del segundo factor" valor={credencial.telefono} />
        )}
      </dl>

      <footer
        className="mt-auto flex flex-wrap items-center justify-between gap-2 border-t pt-2"
        style={{ borderColor: "var(--border)" }}
      >
        <div
          className="flex flex-wrap items-center gap-3 text-xs"
          style={{ color: "var(--fg-muted)" }}
        >
          <MedidorFuerza fuerza={credencial.clave_fuerza} />
          {credencial.ultimo_cambio !== null && (
            <span className="tabular-nums">Cambiada el {comoFecha(credencial.ultimo_cambio)}</span>
          )}
          {credencial.tiene_notas && revelada === undefined && (
            <span className="inline-flex items-center gap-1">
              <StickyNote size={12} aria-hidden={true} />
              Con notas
            </span>
          )}
        </div>
        <div className="flex gap-1">
          <Boton variante="fantasma" tamano="sm" onClick={onBitacora}>
            <History size={14} aria-hidden={true} />
            Bitácora
          </Boton>
          {puedeEditar && (
            <>
              <Boton variante="fantasma" tamano="sm" onClick={onEditar}>
                <Pencil size={14} aria-hidden={true} />
                Editar
              </Boton>
              <Boton
                variante="fantasma"
                tamano="sm"
                onClick={onBorrar}
                aria-label={`Borrar ${credencial.nombre}`}
                style={{ color: "var(--destructive)" }}
              >
                <Trash2 size={14} aria-hidden={true} />
              </Boton>
            </>
          )}
        </div>
      </footer>

      {aviso !== null && (
        <p role="status" className="text-xs" style={{ color: "var(--fg-muted)" }}>
          {aviso}
        </p>
      )}
    </article>
  )
}

function Dato({
  etiqueta,
  valor,
  onCopiar,
}: {
  etiqueta: string
  valor: string
  onCopiar?: () => void
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="min-w-0">
        <dt className="text-xs" style={{ color: "var(--fg-muted)" }}>
          {etiqueta}
        </dt>
        <dd className="break-all">{valor}</dd>
      </div>
      {onCopiar !== undefined && (
        <button
          type="button"
          onClick={onCopiar}
          aria-label={`Copiar ${etiqueta.toLowerCase()}`}
          className="boton-secundario shrink-0 p-2"
        >
          <Copy size={16} aria-hidden={true} />
        </button>
      )}
    </div>
  )
}
